import React, { ReactElement } from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";
import {
  EmailOtpRequestMutationMutation,
  EmailOtpRequestMutationMutationVariables,
} from "../../../../schemaTypes";
import { normalizeErrors } from "../../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";

const mapEmailOtpRequestApolloError = (message: string): NormalizedErrorMap => {
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes("email")) {
    return { email: message };
  }

  return { general: message };
};

const emailOtpRequestMutation = gql`
  mutation EmailOtpRequestMutation($input: EmailOtpRequestInput!) {
    emailOtpRequest(input: $input) {
      errors {
        path
        message
      }
      message
    }
  }
`;

export interface WithEmailOtpRequest {
  emailOtpRequest: (
    values: EmailOtpRequestMutationMutationVariables
  ) => Promise<NormalizedErrorMap | null>;
  loading: boolean;
  error?: Error;
  message?: string;
}

interface Props {
  children: (data: WithEmailOtpRequest) => ReactElement | null;
}

export const EmailOtpRequestController: React.FC<Props> = ({ children }) => {
  const [mutate, { loading, error, data }] = useMutation<
    EmailOtpRequestMutationMutation,
    EmailOtpRequestMutationMutationVariables
  >(emailOtpRequestMutation);

  const submit = async (
    values: EmailOtpRequestMutationMutationVariables
  ): Promise<NormalizedErrorMap | null> => {
    try {
      const { data: { emailOtpRequest } = {} as any }: any = await mutate({ variables: values });

      if (emailOtpRequest?.errors) {
        return normalizeErrors(emailOtpRequest.errors);
      }

      if (!emailOtpRequest) {
        return { general: "An unknown error occurred" };
      }

      return null;
    } catch (mutationError) {
      const apolloError = mutationError as ApolloError;
      const message =
        apolloError.graphQLErrors?.[0]?.message ||
        apolloError.message ||
        "An error occurred";

      return mapEmailOtpRequestApolloError(message);
    }
  };

  const message = data?.emailOtpRequest?.message || null || undefined;

  return children({
    emailOtpRequest: submit,
    loading,
    error,
    message
  });
};

